import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { registerSW } from 'virtual:pwa-register';
import { cn } from '@ui/lib/utils';

const VERSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const FALLBACK_RELOAD_DELAY_MS = 600;

type UpdateReason = 'service-worker' | 'version';

function isValidVersion(version?: string | null): version is string {
  return Boolean(version && version.trim() && !version.startsWith('${'));
}

function getCurrentRuntimeVersion(): string | null {
  if (typeof window === 'undefined') return null;
  const version = window.env?.CAREFORU_VERSION;
  return isValidVersion(version) ? version : null;
}

function extractRuntimeVersion(envScript: string): string | null {
  const match = envScript.match(/CAREFORU_VERSION\s*=\s*["']([^"']+)["']/);
  const version = match?.[1];
  return isValidVersion(version) ? version : null;
}

async function fetchLatestRuntimeVersion(): Promise<string | null> {
  const response = await fetch(`/scripts/env.js?_=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });

  if (!response.ok) return null;
  return extractRuntimeVersion(await response.text());
}

async function clearBrowserCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;

  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
}

export function AppUpdateManager() {
  const [updateReason, setUpdateReason] = useState<UpdateReason | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const currentVersionRef = useRef<string | null>(getCurrentRuntimeVersion());
  const latestVersionRef = useRef<string | null>(null);
  const dismissedVersionRef = useRef<string | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const updateServiceWorkerRef = useRef<((reloadPage?: boolean) => Promise<void>) | null>(null);
  const isReloadingRef = useRef(false);

  const showUpdate = useCallback((reason: UpdateReason, version?: string | null) => {
    if (version && dismissedVersionRef.current === version) return;
    latestVersionRef.current = version ?? null;
    setLatestVersion(version ?? null);
    setUpdateReason(reason);
  }, []);

  const checkRuntimeVersion = useCallback(async () => {
    const currentVersion = currentVersionRef.current;
    if (!currentVersion) return;

    try {
      const remoteVersion = await fetchLatestRuntimeVersion();
      if (!remoteVersion || remoteVersion === currentVersion) return;
      showUpdate('version', remoteVersion);
      void registrationRef.current?.update();
    } catch (error) {
      console.warn('[AppUpdateManager] 버전 확인 실패:', error);
    }
  }, [showUpdate]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    updateServiceWorkerRef.current = registerSW({
      immediate: true,
      onNeedReload() {
        if (isReloadingRef.current) return;
        showUpdate('service-worker', latestVersionRef.current);
      },
      onNeedRefresh() {
        showUpdate('service-worker', latestVersionRef.current);
      },
      onRegisteredSW(_swUrl, registration) {
        registrationRef.current = registration ?? null;
      },
      onRegisterError(error) {
        console.warn('[AppUpdateManager] Service Worker 등록 실패:', error);
      },
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      void checkRuntimeVersion();
      void registrationRef.current?.update();
    };

    const handleFocus = () => {
      void checkRuntimeVersion();
      void registrationRef.current?.update();
    };

    const handleControllerChange = () => {
      if (isReloadingRef.current) return;
      isReloadingRef.current = true;
      window.location.reload();
    };

    const timer = window.setInterval(() => {
      void checkRuntimeVersion();
      void registrationRef.current?.update();
    }, VERSION_CHECK_INTERVAL_MS);

    void checkRuntimeVersion();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [checkRuntimeVersion, showUpdate]);

  const handleDismiss = () => {
    if (latestVersion) {
      dismissedVersionRef.current = latestVersion;
    }
    setUpdateReason(null);
  };

  const handleApplyUpdate = async () => {
    if (isApplying) return;

    setIsApplying(true);
    isReloadingRef.current = true;

    try {
      await registrationRef.current?.update();
      await updateServiceWorkerRef.current?.(true);
      await clearBrowserCaches();
    } catch (error) {
      console.warn('[AppUpdateManager] 업데이트 적용 실패, reload로 전환:', error);
    } finally {
      window.setTimeout(() => {
        window.location.reload();
      }, FALLBACK_RELOAD_DELAY_MS);
    }
  };

  if (!updateReason) return null;

  const versionLabel = latestVersion ? `v${latestVersion}` : '새 버전';

  return (
    <div
      className="fixed inset-x-3 z-[2147483646] mx-auto max-w-lg"
      style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
    >
      <div className="rounded-2xl border border-[#72734e]/20 bg-white p-3 shadow-2xl dark:border-[#d7d8b7]/20 dark:bg-neutral-900">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#72734e]/10 dark:bg-[#d7d8b7]/10">
            <RefreshCw className={cn('h-5 w-5 text-[#72734e] dark:text-[#d7d8b7]', isApplying && 'animate-spin')} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-neutral-900 dark:text-white">업데이트 준비 완료</p>
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
              {versionLabel}을 적용할 수 있습니다
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            disabled={isApplying}
            aria-label="업데이트 알림 닫기"
            title="닫기"
            className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-40 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleApplyUpdate}
          disabled={isApplying}
          className="mt-3 flex min-h-11 w-full items-center justify-center rounded-xl bg-[#72734e] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#646544] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isApplying ? '업데이트 중...' : '업데이트'}
        </button>
      </div>
    </div>
  );
}
